import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Moon,
  Sun,
  FileText,
  ShoppingCart,
  Store,
  FolderOpen,
  MessageSquare,
  Wrench,
  CheckCircle,
  Settings,
  UserPlus,
  Zap,
  Shield,
  Clock,
  Globe,
  Smartphone,
  Plug,
  ArrowRight,
  CreditCard,
  MousePointerClick,
  BadgeCheck,
  Tag,
  Users,
  Building2,
  FileSignature,
  Cpu,
  Mail,
  Phone,
  Database,
  EyeOff,
  Eye,
  Bot,
  Mic,
  Sparkles,
  Headphones,
  Briefcase,
} from 'lucide-react'
import logoEspaceClientTexte from '../images/Logo-Espace-client-texte.png'
import logoEspaceClientTexteDark from '../images/Logo-Espace-client-texte-dark.png'
import { useDarkMode } from '../hooks/useDarkMode'
import { LEGAL_PAGES } from './legal/legalEntity'
import { useSeo } from '../hooks/useSeo'
import api from '../services/api'

// ── Pricing plan types ───────────────────────────────────────────────────────
interface PricingPlan {
  id: number
  name: string
  description: string
  description_fr: string
  description_en: string
  base_price: number
  max_users: number
  max_clients: number
  max_signatures: number
  max_ai_tokens: number
  max_emails: number
  max_sku_free: number
  is_custom: boolean
}

const QUOTA_ITEMS = [
  { icon: Users, key: 'max_users', labelKey: 'landing.pricing.quota.users' },
  { icon: Building2, key: 'max_clients', labelKey: 'landing.pricing.quota.clients' },
  { icon: FileSignature, key: 'max_signatures', labelKey: 'landing.pricing.quota.signatures' },
  { icon: Cpu, key: 'max_ai_tokens', labelKey: 'landing.pricing.quota.aiTokens' },
  { icon: Mail, key: 'max_emails', labelKey: 'landing.pricing.quota.emails' },
  { icon: Store, key: 'max_sku_free', labelKey: 'landing.pricing.quota.catalog' },
]

function fmtQuota(n: number, locale = 'fr-FR') {
  return n === -1 ? '∞' : n.toLocaleString(locale)
}

// Default plans shown while the API loads (keeps the page stable on slow networks)
const DEFAULT_PLANS: PricingPlan[] = [
  {
    id: 1,
    name: 'Starter',
    description: 'Pack idéal pour démarrer, 1 utilisateur, 50 clients',
    description_fr: 'Pack idéal pour démarrer, 1 utilisateur, 50 clients',
    description_en: 'Ideal starter pack, 1 user, 50 clients',
    base_price: 9,
    max_users: 1,
    max_clients: 50,
    max_signatures: 10,
    max_ai_tokens: 0,
    max_emails: 200,
    max_sku_free: 50,
    is_custom: false,
  },
  {
    id: 2,
    name: 'Pro',
    description: 'Pack croissance, 5 utilisateurs, 300 clients',
    description_fr: 'Pack croissance, 5 utilisateurs, 300 clients',
    description_en: 'Growth pack, 5 users, 300 clients',
    base_price: 49,
    max_users: 5,
    max_clients: 300,
    max_signatures: 25,
    max_ai_tokens: 2000000,
    max_emails: 1000,
    max_sku_free: 500,
    is_custom: false,
  },
  {
    id: 4,
    name: 'Gold',
    description: 'Pack avancé, 10 utilisateurs, 1 000 clients',
    description_fr: 'Pack avancé, 10 utilisateurs, 1 000 clients',
    description_en: 'Advanced pack, 10 users, 1 000 clients',
    base_price: 99,
    max_users: 10,
    max_clients: 1000,
    max_signatures: 75,
    max_ai_tokens: 4000000,
    max_emails: 3000,
    max_sku_free: 2000,
    is_custom: false,
  },
  {
    id: 4,
    name: 'Entreprise',
    description: 'Tarification sur devis, quotas sur mesure',
    description_fr: 'Tarification sur devis, quotas sur mesure',
    description_en: 'Custom pricing on quote, personalised quotas',
    base_price: 0,
    max_users: 0,
    max_clients: 0,
    max_signatures: 0,
    max_ai_tokens: 0,
    max_emails: 0,
    max_sku_free: 0,
    is_custom: true,
  },
]

// ── ERP / CRM logos ─────────────────────────────────────────────────────────
// Place logo files in  frontend/public/logos/  (served at /logos/<filename>).
// Supported formats: PNG, SVG, WebP.  Recommended size: ~240 × 80 px, transparent bg.
// The `initials` string is shown as a text badge when the image file is absent.
const erpLogos = [
  {
    src: '/logos/dolibarr.png',
    alt: 'Dolibarr',
    initials: 'D',
    badge: 'bg-orange-100 text-orange-700 border-orange-200',
  },
  {
    src: '/logos/odoo.png',
    alt: 'Odoo',
    initials: 'OD',
    badge: 'bg-purple-100 text-purple-700 border-purple-200',
  },
  {
    src: '/logos/salesforce.png',
    alt: 'Salesforce',
    initials: 'SF',
    badge: 'bg-blue-100 text-blue-700 border-blue-200',
  },
  {
    src: '/logos/business-central.png',
    alt: 'Microsoft Business Central',
    initials: 'BC',
    badge: 'bg-sky-100 text-sky-700 border-sky-200',
  },
  {
    src: '/logos/csv.png',
    alt: 'CSV / Excel',
    initials: 'CSV',
    badge: 'bg-green-100 text-green-700 border-green-200',
  },
  {
    src: '/logos/sage.png',
    alt: 'Sage 100',
    initials: 'S100',
    badge: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  },
  {
    src: '/logos/netsuite.png',
    alt: 'NetSuite',
    initials: 'NS',
    badge: 'bg-red-100 text-red-700 border-red-200',
  },
  {
    src: '/logos/divalto.png',
    alt: 'Divalto Infinity',
    initials: 'DIV',
    badge: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  },
  {
    src: '/logos/erpnext.png',
    alt: 'ERPNext',
    initials: 'ERN',
    badge: 'bg-teal-100 text-teal-700 border-teal-200',
  },
  {
    src: '/logos/zoho.png',
    alt: 'Zoho One',
    initials: 'ZO',
    badge: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  },
  {
    src: '/logos/cegid.png',
    alt: 'Cegid XRP Flex',
    initials: 'CGD',
    badge: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  },
]

// Duplicate 6× so the strip is always wider than the viewport.
// The CSS animation moves by -16.67% (= 1 set = 1/6 of total) → seamless loop.
const MARQUEE_LOGOS = [...erpLogos, ...erpLogos, ...erpLogos, ...erpLogos, ...erpLogos, ...erpLogos]

const featureIcons = [FileText, ShoppingCart, Store, FolderOpen, MessageSquare, Wrench]
const howItWorksIcons = [Settings, UserPlus, Zap]
const advantageIcons = [Shield, Clock, Plug, Smartphone, Globe, CheckCircle]

export default function LandingPage() {
  const { t, i18n } = useTranslation()
  const { isDark, toggle: toggleDarkMode } = useDarkMode()

  useSeo({ title: t('seo.landingTitle'), description: t('seo.landingDescription') })

  // When already authenticated, the header logo leads to the dashboard instead of the landing top
  const isLoggedIn = Boolean(localStorage.getItem('auth_token'))

  // Track logo images that fail to load so we can render the text fallback
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set())
  const markFailed = (src: string) => setFailedImages((prev) => new Set(prev).add(src))

  // Pricing plans (fetched from backend, fallback to defaults)
  const [plans, setPlans] = useState<PricingPlan[]>(DEFAULT_PLANS)
  // Pack quantities per plan (planId → qty), defaults to 1
  const [quantities, setQuantities] = useState<Record<number, number>>({})
  const getQty = (planId: number) => quantities[planId] ?? 1
  const setQty = (planId: number, qty: number) =>
    setQuantities((prev) => ({ ...prev, [planId]: Math.max(1, qty) }))

  useEffect(() => {
    api
      .getPublicPricingPlans()
      .then((data) => {
        if (data.length > 0) setPlans(data)
      })
      .catch(() => {})
  }, [])

  const features = [
    {
      titleKey: 'landing.features.invoices.title',
      descKey: 'landing.features.invoices.description',
    },
    { titleKey: 'landing.features.orders.title', descKey: 'landing.features.orders.description' },
    { titleKey: 'landing.features.catalog.title', descKey: 'landing.features.catalog.description' },
    { titleKey: 'landing.features.gallery.title', descKey: 'landing.features.gallery.description' },
    { titleKey: 'landing.features.tickets.title', descKey: 'landing.features.tickets.description' },
    {
      titleKey: 'landing.features.revisions.title',
      descKey: 'landing.features.revisions.description',
    },
  ]

  const advantages = [
    'landing.advantages.secure',
    'landing.advantages.realtime',
    'landing.advantages.compatible',
    'landing.advantages.responsive',
    'landing.advantages.available',
    'landing.advantages.branded',
  ]

  const stats = [
    {
      labelKey: 'landing.stats.invoicesLabel',
      valueKey: 'landing.stats.invoicesValue',
      color: 'text-brand-600',
    },
    {
      labelKey: 'landing.stats.setupLabel',
      valueKey: 'landing.stats.setupValue',
      color: 'text-aqua-600',
    },
    {
      labelKey: 'landing.stats.erpLabel',
      valueKey: 'landing.stats.erpValue',
      color: 'text-brand-800',
    },
    {
      labelKey: 'landing.stats.securityLabel',
      valueKey: 'landing.stats.securityValue',
      color: 'text-aqua-700',
    },
  ]

  const howItWorksSteps = [
    { titleKey: 'landing.howItWorks.step1.title', descKey: 'landing.howItWorks.step1.desc' },
    { titleKey: 'landing.howItWorks.step2.title', descKey: 'landing.howItWorks.step2.desc' },
    { titleKey: 'landing.howItWorks.step3.title', descKey: 'landing.howItWorks.step3.desc' },
  ]

  return (
    <div className="landing-brand min-h-screen bg-white dark:bg-gray-900">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header className="bg-white/90 dark:bg-gray-900/90 backdrop-blur border-b border-gray-100 dark:border-gray-700 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link
            to={isLoggedIn ? '/dashboard' : '/'}
            onClick={() => {
              if (!isLoggedIn) window.scrollTo({ top: 0, behavior: 'smooth' })
            }}
            aria-label={isLoggedIn ? t('nav.dashboard') : t('landing.backToTop')}
            title={isLoggedIn ? t('nav.dashboard') : t('landing.backToTop')}
            className="group flex items-center rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
          >
            <img
              src={logoEspaceClientTexte}
              alt="Espace Client"
              className="h-9 w-auto transition-transform group-hover:scale-105 dark:hidden"
            />
            <img
              src={logoEspaceClientTexteDark}
              alt=""
              aria-hidden="true"
              className="hidden h-9 w-auto transition-transform group-hover:scale-105 dark:block"
            />
          </Link>

          <div className="flex items-center gap-3">
            <a
              href="#cas-usage"
              className="hidden md:flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-brand-600 dark:hover:text-brand-400 transition-colors rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <Sparkles className="w-4 h-4" /> {t('landing.useCases.badge')}
            </a>

            <a
              href="#faq"
              className="hidden md:flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-brand-600 dark:hover:text-brand-400 transition-colors rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <MessageSquare className="w-4 h-4" /> {t('landing.faq.badge')}
            </a>

            <a
              href="#tarifs"
              className="hidden sm:flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-brand-600 dark:hover:text-brand-400 transition-colors rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <Tag className="w-4 h-4" /> {t('landing.pricing.navLink')}
            </a>

            <button
              onClick={toggleDarkMode}
              title={t('darkMode.toggle')}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {isDark ? (
                <Sun className="w-4 h-4 text-amber-400" />
              ) : (
                <Moon className="w-4 h-4 text-gray-600" />
              )}
            </button>

            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              {['fr', 'en'].map((lang) => (
                <button
                  key={lang}
                  onClick={() => i18n.changeLanguage(lang)}
                  className={`px-2 py-1 text-xs font-semibold rounded-md transition-all ${
                    i18n.language === lang || i18n.language?.startsWith(lang)
                      ? 'bg-white dark:bg-gray-800 text-brand-700 dark:text-brand-300 shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                >
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>

            <Link
              to="/login"
              className="flex items-center gap-2 px-5 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors font-medium text-sm"
            >
              {t('landing.signIn')}
            </Link>
          </div>
        </div>
      </header>

      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-brand-950 text-white">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-brand-600/30 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 -left-20 w-72 h-72 bg-aqua-500/20 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-6xl mx-auto px-6 py-28 text-center">
          <span className="inline-flex items-center gap-2 mb-5 px-3 py-1 text-xs font-semibold bg-white/10 border border-white/15 rounded-full tracking-wide uppercase">
            <Users className="w-3.5 h-3.5 text-brand-300" />
            {t('landing.hero.badge')}
          </span>
          <h1 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight tracking-tight whitespace-pre-line">
            {t('landing.hero.title')}
          </h1>
          <p className="text-lg md:text-xl text-brand-200 mb-8 max-w-3xl mx-auto leading-relaxed">
            {t('landing.hero.subtitle')}
          </p>

          {/* Dual audience at a glance: end customers + client-facing teams */}
          <div className="flex flex-wrap items-center justify-center gap-2.5 mb-10">
            {(
              [
                { icon: Users, key: 'landing.hero.tagClients' },
                { icon: ShoppingCart, key: 'landing.hero.tagAdv' },
                { icon: Headphones, key: 'landing.hero.tagSav' },
                { icon: Briefcase, key: 'landing.hero.tagSales' },
              ] as const
            ).map(({ icon: Icon, key }) => (
              <span
                key={key}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-white/10 border border-white/15 text-sm font-medium text-white/90"
              >
                <Icon className="w-3.5 h-3.5 text-aqua-300" />
                {t(key)}
              </span>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-8 py-4 bg-brand-600 text-white rounded-xl font-bold text-base whitespace-nowrap hover:bg-brand-500 transition-colors shadow-lg shadow-brand-600/30"
            >
              {t('landing.hero.cta')}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white rounded-xl font-semibold text-base whitespace-nowrap hover:bg-white/10 hover:border-white/60 transition-colors"
            >
              {t('landing.cta.registerButton')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Who it is for: end customers AND client-facing teams ─────── */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="inline-block mb-4 px-3 py-1 text-xs font-semibold bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 rounded-full tracking-wide uppercase">
              {t('landing.audience.badge')}
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white whitespace-pre-line leading-tight mb-4">
              {t('landing.audience.title')}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 max-w-2xl mx-auto text-base leading-relaxed">
              {t('landing.audience.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Audience 1 — the end customer */}
            <div className="bg-brand-50 dark:bg-gray-800 rounded-xl p-8 border border-brand-100 dark:border-gray-700 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-xl bg-brand-600 flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    {t('landing.audience.clients.title')}
                  </h3>
                  <p className="text-xs font-semibold uppercase tracking-wide text-brand-600 dark:text-brand-300">
                    {t('landing.audience.clients.tag')}
                  </p>
                </div>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-5">
                {t('landing.audience.clients.desc')}
              </p>
              <ul className="space-y-2.5">
                {(['point1', 'point2', 'point3', 'point4'] as const).map((k) => (
                  <li key={k} className="flex items-start gap-2.5">
                    <CheckCircle className="w-4 h-4 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {t(`landing.audience.clients.${k}`)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Audience 2 — the client-facing teams (order desk, after-sales, sales) */}
            <div className="bg-brand-950 rounded-xl p-8 border border-brand-900 shadow-sm text-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-xl bg-aqua-500 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">{t('landing.audience.teams.title')}</h3>
                  <p className="text-xs font-semibold uppercase tracking-wide text-aqua-300">
                    {t('landing.audience.teams.tag')}
                  </p>
                </div>
              </div>
              <p className="text-sm text-brand-200 leading-relaxed mb-5">
                {t('landing.audience.teams.desc')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {(
                  [
                    { icon: ShoppingCart, role: 'adv' },
                    { icon: Headphones, role: 'sav' },
                    { icon: Briefcase, role: 'sales' },
                  ] as const
                ).map(({ icon: Icon, role }) => (
                  <div
                    key={role}
                    className="rounded-xl bg-white/5 border border-white/10 p-4 hover:bg-white/10 transition-colors"
                  >
                    <Icon className="w-5 h-5 text-aqua-300 mb-2.5" />
                    <p className="font-semibold text-sm mb-1">
                      {t(`landing.audience.teams.${role}Title`)}
                    </p>
                    <p className="text-xs text-brand-200 leading-relaxed">
                      {t(`landing.audience.teams.${role}Desc`)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 360° view: pain points + benefits ─────────────────────────── */}
      <section className="py-20 bg-brand-50 dark:bg-gray-800/50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="inline-block mb-4 px-3 py-1 text-xs font-semibold bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 rounded-full tracking-wide uppercase">
              {t('landing.vue360.badge')}
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white whitespace-pre-line leading-tight mb-4">
              {t('landing.vue360.title')}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 max-w-2xl mx-auto text-base leading-relaxed">
              {t('landing.vue360.subtitle')}
            </p>
          </div>

          {/* Pain points */}
          <h3 className="text-sm font-semibold uppercase tracking-wide text-red-500 dark:text-red-400 text-center mb-6">
            {t('landing.vue360.painTitle')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
            {(
              [
                { icon: Database, titleKey: 'pain1Title', descKey: 'pain1Desc' },
                { icon: MessageSquare, titleKey: 'pain2Title', descKey: 'pain2Desc' },
                { icon: EyeOff, titleKey: 'pain3Title', descKey: 'pain3Desc' },
              ] as const
            ).map(({ icon: Icon, titleKey, descKey }) => (
              <div
                key={titleKey}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-red-100 dark:border-red-900/40 shadow-sm"
              >
                <div className="w-10 h-10 rounded-lg bg-red-50 dark:bg-red-900/30 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-red-500 dark:text-red-400" />
                </div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                  {t(`landing.vue360.${titleKey}`)}
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                  {t(`landing.vue360.${descKey}`)}
                </p>
              </div>
            ))}
          </div>

          {/* Benefits — the 360° answer */}
          <h3 className="text-sm font-semibold uppercase tracking-wide text-brand-600 dark:text-brand-400 text-center mb-6">
            {t('landing.vue360.benefitsTitle')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(
              [
                { icon: Users, titleKey: 'benefit1Title', descKey: 'benefit1Desc' },
                { icon: Eye, titleKey: 'benefit2Title', descKey: 'benefit2Desc' },
                { icon: Plug, titleKey: 'benefit3Title', descKey: 'benefit3Desc' },
              ] as const
            ).map(({ icon: Icon, titleKey, descKey }) => (
              <div
                key={titleKey}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-brand-100 dark:border-brand-900/40 shadow-sm"
              >
                <div className="w-10 h-10 rounded-lg bg-brand-50 dark:bg-brand-900/30 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-brand-600 dark:text-brand-400" />
                </div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                  {t(`landing.vue360.${titleKey}`)}
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                  {t(`landing.vue360.${descKey}`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ERP Logo Marquee ──────────────────────────────────────────── */}
      <section className="py-14 bg-white-forced overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 text-center mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ color: '#111827' }}>
            {t('landing.erp.title')}
          </h2>
          <p className="text-sm max-w-xl mx-auto" style={{ color: '#6b7280' }}>
            {t('landing.erp.subtitle')}
          </p>
        </div>

        {/* Scrolling strip */}
        <div className="relative overflow-hidden bg-white-forced">
          {/* Fade masks — match the section background */}
          <div
            className="absolute left-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
            style={{ background: 'linear-gradient(to right, #ffffff, transparent)' }}
          />
          <div
            className="absolute right-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
            style={{ background: 'linear-gradient(to left, #ffffff, transparent)' }}
          />

          {/* The animated strip — contains 2 identical sets of logos for seamless loop.
              width: max-content is required so translateX(-50%) resolves to exactly
              one set width on all browsers (Safari iOS otherwise computes 50% of 0). */}
          <div className="flex items-center animate-marquee-ltr" style={{ width: 'max-content' }}>
            {[0, 1].flatMap((rep) =>
              MARQUEE_LOGOS.map((logo, idx) => (
                <div
                  key={`${rep}-${idx}`}
                  aria-hidden={rep === 1}
                  className="flex-shrink-0 mx-12 flex items-center justify-center"
                  style={{ minWidth: 160 }}
                >
                  {failedImages.has(logo.src) ? (
                    /* Text badge fallback when the image file is missing */
                    <span
                      className={`px-5 py-2.5 rounded-xl font-bold text-base border ${logo.badge}`}
                    >
                      {logo.alt}
                    </span>
                  ) : (
                    <img
                      src={logo.src}
                      alt={logo.alt}
                      className="h-16 max-w-[200px] object-contain drop-shadow-sm hover:scale-105 transition-transform duration-300"
                      onError={() => markFailed(logo.src)}
                    />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* ── Features ──────────────────────────────────────────────────── */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white text-center mb-3">
            {t('landing.features.title')}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 text-center mb-12 max-w-xl mx-auto">
            {t('landing.features.subtitle')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => {
              const Icon = featureIcons[i]
              return (
                <div
                  key={f.titleKey}
                  className="group bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md hover:border-brand-100 dark:hover:border-brand-700 transition-all"
                >
                  <div className="w-10 h-10 rounded-lg bg-brand-50 dark:bg-gray-700 flex items-center justify-center mb-4 group-hover:bg-brand-100 dark:group-hover:bg-gray-600 transition-colors">
                    <Icon className="w-5 h-5 text-brand-600 dark:text-brand-400" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                    {t(f.titleKey)}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                    {t(f.descKey)}
                  </p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ── Online Payment spotlight ──────────────────────────────────── */}
      <section className="py-20 bg-gradient-to-br from-aqua-50 via-white to-brand-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 overflow-hidden">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="inline-block mb-4 px-3 py-1 text-xs font-semibold bg-aqua-100 dark:bg-aqua-900/40 text-aqua-700 dark:text-aqua-300 rounded-full tracking-wide uppercase">
              {t('landing.payment.badge')}
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white whitespace-pre-line leading-tight mb-4">
              {t('landing.payment.title')}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 max-w-2xl mx-auto text-base leading-relaxed">
              {t('landing.payment.subtitle')}
            </p>
          </div>

          {/* 3-step flow */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {(
              [
                {
                  icon: MousePointerClick,
                  color: 'bg-brand-100 dark:bg-brand-900/30 text-brand-600 dark:text-brand-300',
                  titleKey: 'landing.payment.step1Title',
                  descKey: 'landing.payment.step1Desc',
                },
                {
                  icon: CreditCard,
                  color: 'bg-aqua-100 dark:bg-aqua-900/30 text-aqua-500 dark:text-aqua-300',
                  titleKey: 'landing.payment.step2Title',
                  descKey: 'landing.payment.step2Desc',
                },
                {
                  icon: BadgeCheck,
                  color: 'bg-brand-200 dark:bg-brand-900/40 text-brand-900 dark:text-brand-200',
                  titleKey: 'landing.payment.step3Title',
                  descKey: 'landing.payment.step3Desc',
                },
              ] as const
            ).map((step, i) => {
              const Icon = step.icon
              return (
                <div
                  key={i}
                  className="relative bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-100 dark:border-gray-700 shadow-sm"
                >
                  <span className="absolute top-4 right-4 text-2xl font-extrabold text-gray-100 dark:text-gray-700 select-none">
                    {i + 1}
                  </span>
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center mb-4 ${step.color}`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                    {t(step.titleKey)}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                    {t(step.descKey)}
                  </p>
                </div>
              )
            })}
          </div>

          {/* Trust + perks */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-brand-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-gray-900 dark:text-white text-sm">
                  {t('landing.payment.trust')}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {t('landing.payment.trustSub')}
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 text-sm">
              {(
                [
                  'landing.payment.recurring',
                  'landing.payment.onboarding',
                  'landing.payment.noFees',
                ] as const
              ).map((key) => (
                <span
                  key={key}
                  className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300"
                >
                  <CheckCircle className="w-4 h-4 text-aqua-500 flex-shrink-0" />
                  {t(key)}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── AI shop assistant spotlight ───────────────────────────────── */}
      <section className="py-20 bg-brand-50 dark:bg-gray-900">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="inline-flex items-center gap-1.5 mb-4 px-3 py-1 text-xs font-semibold bg-aqua-100 dark:bg-aqua-900/40 text-aqua-700 dark:text-aqua-300 rounded-full tracking-wide uppercase">
              <Sparkles className="w-3.5 h-3.5" /> {t('landing.aiAssistant.badge')}
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white whitespace-pre-line leading-tight mb-4">
              {t('landing.aiAssistant.title')}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 max-w-2xl mx-auto text-base leading-relaxed">
              {t('landing.aiAssistant.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(
              [
                {
                  icon: Bot,
                  color: 'bg-brand-100 dark:bg-brand-900/30 text-brand-600 dark:text-brand-300',
                  titleKey: 'landing.aiAssistant.chatTitle',
                  descKey: 'landing.aiAssistant.chatDesc',
                },
                {
                  icon: Mic,
                  color: 'bg-aqua-100 dark:bg-aqua-900/30 text-aqua-500 dark:text-aqua-300',
                  titleKey: 'landing.aiAssistant.voiceTitle',
                  descKey: 'landing.aiAssistant.voiceDesc',
                },
                {
                  icon: Store,
                  color: 'bg-brand-200 dark:bg-brand-900/40 text-brand-900 dark:text-brand-200',
                  titleKey: 'landing.aiAssistant.catalogTitle',
                  descKey: 'landing.aiAssistant.catalogDesc',
                },
              ] as const
            ).map(({ icon: Icon, color, titleKey, descKey }) => (
              <div
                key={titleKey}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-100 dark:border-gray-700 shadow-sm"
              >
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center mb-4 ${color}`}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{t(titleKey)}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                  {t(descKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ──────────────────────────────────────────────── */}
      <section className="py-20 bg-brand-950 text-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-12">{t('landing.howItWorks.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {howItWorksSteps.map((step, i) => {
              const Icon = howItWorksIcons[i]
              return (
                <div
                  key={step.titleKey}
                  className="relative flex flex-col items-center text-center"
                >
                  <div className="relative w-16 h-16 rounded-xl bg-brand-600 text-white flex items-center justify-center shadow-lg shadow-brand-600/30 mb-4">
                    <Icon className="w-7 h-7" />
                    <span className="absolute -top-2 -right-2 w-6 h-6 bg-white border-2 border-aqua-400 text-brand-950 rounded-full text-xs font-extrabold flex items-center justify-center shadow">
                      {i + 1}
                    </span>
                  </div>
                  <h3 className="font-bold mb-2">{t(step.titleKey)}</h3>
                  <p className="text-brand-200 text-sm leading-relaxed">{t(step.descKey)}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ── Use cases (client scenarios by industry) ──────────────────── */}
      <section id="cas-usage" className="py-20 bg-white dark:bg-gray-900 scroll-mt-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="inline-block mb-4 px-3 py-1 text-xs font-semibold bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 rounded-full tracking-wide uppercase">
              {t('landing.useCases.badge')}
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white whitespace-pre-line leading-tight mb-4">
              {t('landing.useCases.title')}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 max-w-2xl mx-auto text-base leading-relaxed">
              {t('landing.useCases.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(
              [
                { icon: Store, key: 'uc1' },
                { icon: FileSignature, key: 'uc2' },
                { icon: Wrench, key: 'uc3' },
                { icon: CreditCard, key: 'uc4' },
                { icon: Building2, key: 'uc5' },
                { icon: Bot, key: 'uc6' },
              ] as const
            ).map(({ icon: Icon, key }) => (
              <div
                key={key}
                className="flex flex-col bg-brand-50 dark:bg-gray-800 rounded-xl p-6 border border-brand-100 dark:border-gray-700 shadow-sm"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-xl bg-brand-600 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                    {t(`landing.useCases.${key}.title`)}
                  </h3>
                </div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">
                  {t('landing.useCases.contextLabel')}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
                  {t(`landing.useCases.${key}.context`)}
                </p>
                <p className="text-xs font-semibold uppercase tracking-wide text-brand-600 dark:text-brand-400 mb-1 mt-auto">
                  {t('landing.useCases.resultLabel')}
                </p>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
                    {t(`landing.useCases.${key}.result`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Tarifs ────────────────────────────────────────────────────── */}
      <section id="tarifs" className="py-20 bg-white dark:bg-gray-900 scroll-mt-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="inline-flex items-center gap-1.5 mb-4 px-3 py-1 text-xs font-semibold bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 rounded-full tracking-wide uppercase">
              <Tag className="w-3.5 h-3.5" /> {t('landing.pricing.badge')}
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white mb-4">
              {t('landing.pricing.title')}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 max-w-xl mx-auto text-base leading-relaxed">
              {t('landing.pricing.subtitle')}
            </p>
          </div>

          {/* Plan cards */}
          <div
            className={`grid grid-cols-1 sm:grid-cols-2 ${plans.length >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-6 mb-10`}
          >
            {plans.map((plan, i) => {
              const isPopular = plan.name === 'Pro'
              if (plan.is_custom) {
                return (
                  <div
                    key={`plan-custom-${i}`}
                    className="relative flex flex-col rounded-xl border-2 border-gray-200 dark:border-gray-700 p-6
                    bg-gradient-to-br from-gray-900 to-gray-800 text-white"
                  >
                    <div className="mb-4">
                      <h3 className="text-xl font-bold">{plan.name}</h3>
                      <p className="text-sm text-gray-300 mt-1">
                        {(i18n.language.startsWith('en')
                          ? plan.description_en
                          : plan.description_fr) || plan.description}
                      </p>
                    </div>
                    <div className="text-3xl font-extrabold mb-1">
                      {t('landing.pricing.onQuote')}
                    </div>
                    <p className="text-sm text-gray-400 mb-6">
                      {t('landing.pricing.customQuotas')}
                    </p>
                    <div className="space-y-2 mb-6 flex-1">
                      {QUOTA_ITEMS.map(({ icon: Icon, labelKey }) => (
                        <div
                          key={labelKey}
                          className="flex items-center gap-2 text-sm text-gray-300"
                        >
                          <Icon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                          <span>
                            {t(labelKey)} :{' '}
                            <strong className="text-white">
                              {t('landing.pricing.customValue')}
                            </strong>
                          </span>
                        </div>
                      ))}
                    </div>
                    <Link
                      to={`/register?type=supplier&plan_id=${plan.id}`}
                      className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl font-semibold text-sm bg-white text-gray-900 hover:bg-gray-100 transition-colors"
                    >
                      <Phone className="w-4 h-4" /> {t('landing.pricing.contactUs')}
                    </Link>
                  </div>
                )
              }

              {
                const qty = getQty(plan.id)
                const total = Number(plan.base_price) * qty
                return (
                  <div
                    key={plan.id}
                    className={`relative flex flex-col rounded-xl border-2 p-6 transition-all
                    ${
                      isPopular
                        ? 'border-brand-500 shadow-xl shadow-purple-100 dark:shadow-purple-900/30 bg-brand-50 dark:bg-brand-900/10'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                    }`}
                  >
                    {isPopular && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-xs font-bold bg-brand-600 text-white shadow">
                        {t('landing.pricing.popular')}
                      </span>
                    )}
                    <div className="mb-4">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                        {plan.name}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {(i18n.language.startsWith('en')
                          ? plan.description_en
                          : plan.description_fr) || plan.description}
                      </p>
                    </div>

                    {/* Price display — updates with quantity */}
                    <div className="flex items-baseline gap-1 mb-0.5">
                      <span className="text-3xl font-extrabold text-gray-900 dark:text-white">
                        {total.toFixed(0)} €
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {t('landing.pricing.perMonth')}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mb-5">
                      {qty > 1
                        ? t('landing.pricing.billingMultiple', {
                            count: qty,
                            price: Number(plan.base_price).toFixed(0),
                          })
                        : t('landing.pricing.billingMonthly')}
                    </p>

                    {/* Quota list */}
                    <div className="space-y-2 mb-5 flex-1">
                      {QUOTA_ITEMS.map(({ icon: Icon, key, labelKey }) => {
                        const val = plan[key as keyof PricingPlan] as number
                        return (
                          <div
                            key={key}
                            className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
                          >
                            <Icon className="w-4 h-4 text-brand-400 flex-shrink-0" />
                            <span>
                              {t(labelKey)} :{' '}
                              <strong>
                                {fmtQuota(qty > 1 && val > 0 ? val * qty : val, i18n.language)}
                              </strong>
                            </span>
                          </div>
                        )
                      })}
                    </div>

                    {/* Pack quantity selector */}
                    <div className="flex items-center justify-between mb-4 px-1">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {t('landing.pricing.packCount')}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setQty(plan.id, qty - 1)}
                          className="w-7 h-7 rounded-full border border-gray-300 dark:border-gray-600 flex items-center justify-center
                            text-sm hover:border-brand-400 hover:text-brand-600 dark:text-white transition-colors"
                        >
                          −
                        </button>
                        <span className="w-6 text-center font-bold text-gray-900 dark:text-white">
                          {qty}
                        </span>
                        <button
                          onClick={() => setQty(plan.id, qty + 1)}
                          className="w-7 h-7 rounded-full border border-gray-300 dark:border-gray-600 flex items-center justify-center
                            text-sm hover:border-brand-400 hover:text-brand-600 dark:text-white transition-colors"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <Link
                      to={`/register?type=supplier&plan_id=${plan.id}&quantity=${qty}`}
                      className={`flex items-center justify-center gap-2 w-full py-2.5 rounded-xl font-semibold text-sm transition-colors
                        ${
                          isPopular
                            ? 'bg-brand-600 text-white hover:bg-brand-700'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-white hover:bg-brand-50 dark:hover:bg-brand-900/20'
                        }`}
                    >
                      {t('landing.pricing.start')} <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                )
              }
            })}
          </div>

          {/* Additional credits purchase explanation + 1€ = 1000 crédits note */}
          <div className="max-w-2xl mx-auto space-y-2 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {t('landing.pricing.creditsBuyNote')}
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <CreditCard className="w-4 h-4 text-brand-400 flex-shrink-0" />
              <span>{t('landing.pricing.creditsNote')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Advantages + Stats ────────────────────────────────────────── */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4 whitespace-pre-line">
                {t('landing.advantages.title')}
              </h2>
              <p className="text-gray-500 dark:text-gray-400 mb-8 leading-relaxed">
                {t('landing.advantages.subtitle')}
              </p>
              <ul className="space-y-3">
                {advantages.map((key, i) => {
                  const Icon = advantageIcons[i]
                  return (
                    <li key={key} className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-brand-50 dark:bg-gray-700 flex-shrink-0 flex items-center justify-center mt-0.5">
                        <Icon className="w-3.5 h-3.5 text-brand-600 dark:text-brand-400" />
                      </span>
                      <span className="text-gray-700 dark:text-gray-300 text-sm">{t(key)}</span>
                    </li>
                  )
                })}
              </ul>
            </div>

            <div className="bg-gradient-to-br from-brand-50 to-aqua-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-8 border border-brand-100 dark:border-gray-600 shadow-sm">
              {stats.map((stat) => (
                <div
                  key={stat.labelKey}
                  className="flex items-center justify-between py-3.5 border-b border-brand-100 dark:border-gray-600 last:border-0"
                >
                  <span className="text-gray-600 dark:text-gray-300 text-sm">
                    {t(stat.labelKey)}
                  </span>
                  <span className={`font-bold text-base ${stat.color}`}>{t(stat.valueKey)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ (SEO + GEO: FAQPage JSON-LD mirrors this in index.html) ── */}
      <section id="faq" className="py-20 bg-brand-50 dark:bg-gray-800/50 scroll-mt-16">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block mb-4 px-3 py-1 text-xs font-semibold bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 rounded-full tracking-wide uppercase">
              {t('landing.faq.badge')}
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight mb-4">
              {t('landing.faq.title')}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-base leading-relaxed">
              {t('landing.faq.subtitle')}
            </p>
          </div>

          <div className="space-y-4">
            {(['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7', 'q8'] as const).map((k) => (
              <details
                key={k}
                className="group bg-white dark:bg-gray-800 rounded-xl border border-brand-100 dark:border-gray-700 shadow-sm overflow-hidden"
              >
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none p-5 font-semibold text-gray-900 dark:text-white">
                  <span>{t(`landing.faq.${k}.q`)}</span>
                  <ArrowRight className="w-4 h-4 flex-shrink-0 text-brand-600 dark:text-brand-400 transition-transform group-open:rotate-90" />
                </summary>
                <p className="px-5 pb-5 -mt-1 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                  {t(`landing.faq.${k}.a`)}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────────────── */}
      <section className="py-20 bg-gradient-to-br from-brand-950 via-brand-900 to-brand-800 text-white text-center">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-3xl font-extrabold mb-4">{t('landing.cta.title')}</h2>
          <p className="text-brand-100 mb-8 text-base">{t('landing.cta.subtitle')}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-8 py-4 bg-brand-600 text-white rounded-xl font-bold whitespace-nowrap hover:bg-brand-500 transition-colors shadow-lg shadow-brand-600/30"
            >
              {t('landing.cta.button')}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-4 text-white rounded-xl font-bold whitespace-nowrap border-2 border-white/30 hover:bg-white/10 hover:border-white/60 transition-colors"
            >
              {t('landing.cta.registerButton')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <p className="mt-6 text-sm text-brand-200">{t('landing.cta.registerHint')}</p>
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────────────── */}
      <footer className="py-8 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-6xl mx-auto px-6 flex flex-col gap-4 text-sm text-gray-400 dark:text-gray-500">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
            <p>{t('landing.footer')}</p>
            <p>
              {t('landing.footerService')}{' '}
              <a
                href="https://www.iseffi.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-600 hover:text-brand-700 hover:underline font-medium"
              >
                www.iseffi.com
              </a>
            </p>
          </div>

          {/* Mandatory legal pages — reachable from every page of the public site. */}
          <nav className="flex flex-wrap items-center justify-center sm:justify-start gap-x-4 gap-y-2 border-t border-gray-100 dark:border-gray-800 pt-4">
            {LEGAL_PAGES.map((page) => (
              <Link
                key={page.path}
                to={page.path}
                className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
              >
                {t(page.labelKey)}
              </Link>
            ))}
          </nav>
        </div>
      </footer>
    </div>
  )
}
